import { z } from "zod";
import {
  isoDateTimeString,
  pagePaginationQuerySchema,
  pagedResponseSchema,
  uuidSchema,
} from "./common";
import {
  availableFromSchema,
  educationLevelSchema,
  employmentTypeSchema,
  genderSchema,
  languageEntrySchema,
  livingArrangementSchema,
  provinceCodeSchema,
  publicWorkerSummarySchema,
  salaryTypeSchema,
  workerExperienceSchema,
  workerPositionSchema,
} from "./users";
import { kycStatusSchema } from "./kyc";
import { postingSchema } from "./postings";

const salaryOutputSchema = z.union([z.string(), z.number(), z.bigint()]);

export const applicationStateSchema = z.enum([
  "APPLIED",
  "INVITED",
  "ACCEPTED",
  "COMMITTED",
  "REJECTED",
  "WITHDRAWN",
  "EXPIRED",
]);

export const applicationEventTypeSchema = z.enum([
  "APPLIED",
  "MERCHANT_VIEWED",
  "INVITED",
  "INVITE_ACCEPTED",
  "INVITE_COMMITTED",
  "INVITE_CANCELLED_IN_WINDOW",
  "INVITE_CANCELLED_POST_COMMIT",
  "REJECTED",
  "WITHDRAWN",
  "EXPIRED",
]);

export const applicationEventSchema = z.object({
  type: applicationEventTypeSchema,
  createdAt: isoDateTimeString,
});

/** Whether the application was created by the worker (apply) or by an employer
 *  sourcing an open-to-work worker ("Tìm ứng viên", docs/01-product.md). */
export const applicationSourceSchema = z.enum([
  "WORKER_APPLIED",
  "EMPLOYER_SOURCED",
]);
export type ApplicationSource = z.infer<typeof applicationSourceSchema>;

export const applicationSchema = z.object({
  id: uuidSchema,
  postingId: uuidSchema,
  workerId: uuidSchema,
  state: applicationStateSchema,
  source: applicationSourceSchema,
  appliedAt: isoDateTimeString,
  decidedAt: isoDateTimeString.nullable().optional(),
  note: z.string().nullable().optional(),
});

export const applyPostingRequestSchema = z.object({
  note: z.string().max(500).optional(),
});

export const applyPostingResponseSchema = z.object({
  application: applicationSchema,
});

export const inviteSummarySchema = z.object({
  id: uuidSchema,
  status: z.enum([
    "LOCKED",
    "ACCEPTED",
    "COMMITTED",
    "REJECTED",
    "EXPIRED",
    "CANCELLED",
  ]),
  expiresAt: isoDateTimeString,
  acceptedAt: isoDateTimeString.nullable(),
  committedAt: isoDateTimeString.nullable(),
  acceptedSlotStartAt: isoDateTimeString.nullable(),
  orgAddressAlias: z.string().nullable(),
});

export const myApplicationListItemSchema = applicationSchema.extend({
  posting: postingSchema,
  invite: inviteSummarySchema.nullable().optional(),
  committedContact: z
    .object({
      contactPhone: z.string(),
      contactName: z.string(),
      address: z.string(),
      orgName: z.string(),
    })
    .nullable()
    .optional(),
});

export const myApplicationListResponseSchema = z.object({
  items: z.array(myApplicationListItemSchema),
  nextCursor: z.string().nullable(),
});

// A committed interview stays "needs action" (worker should show up / check the
// contact) until this long after the slot starts. Shared by the API count and
// the clients' list filter so the tab badge matches the tab content.
export const COMMITTED_ACTION_WINDOW_MS = 4 * 60 * 60 * 1000;

// GET /me/applications/counts — badge numbers for the worker's two tabs.
// actionNeeded = INVITED + ACCEPTED + COMMITTED still inside the window above.
export const myApplicationCountsSchema = z.object({
  actionNeeded: z.number().int().nonnegative(),
  all: z.number().int().nonnegative(),
});
export type MyApplicationCounts = z.infer<typeof myApplicationCountsSchema>;

// Detail = list-item shape + ordered timeline of events.
// Used by GET /me/applications/:id (worker-scoped).
export const myApplicationDetailSchema = myApplicationListItemSchema.extend({
  events: z.array(applicationEventSchema),
});

export const applicantRowSchema = z.object({
  application: applicationSchema,
  worker: publicWorkerSummarySchema,
  // True once the employer has sent an invite, regardless of whether it has
  // committed yet. Drives the row's invite/pending state independently of
  // `contact` — which only appears after commit (see below).
  invited: z.boolean(),
  committedAt: isoDateTimeString.nullable(),
  // Phone/email are only populated once the invite is COMMITTED (the worker's
  // 30-min commit window has elapsed). Before commit this is null even though
  // `invited` is true.
  contact: z
    .object({
      phone: z.string().nullable(),
      email: z.string().nullable(),
    })
    .nullable(),
});

export const applicantListResponseSchema = z.object({
  items: z.array(applicantRowSchema),
  nextCursor: z.string().nullable(),
});

// ----------------------------------------------------------------------------
// Employer global applicant list ("Danh sách ứng viên" — web-employer).
// Unlike `applicantListResponseSchema` (per-posting, cursor) this spans every
// posting in the org, is filtered by tab/location/search and uses page-number
// pagination. Each row carries its posting + (when scheduled) interview slot.
// ----------------------------------------------------------------------------

/** The four employer tabs map onto application states. */
export const employerApplicantTabSchema = z.enum([
  "new", // APPLIED
  "invited", // INVITED (sent, worker has not picked a slot yet)
  "scheduled", // ACCEPTED + COMMITTED (worker picked a slot)
  "rejected", // REJECTED + EXPIRED + WITHDRAWN
]);
export type EmployerApplicantTab = z.infer<typeof employerApplicantTabSchema>;

/** A confirmed/proposed interview slot embedded in a "scheduled" row. */
export const applicantScheduledSlotSchema = z.object({
  startAt: isoDateTimeString,
  endAt: isoDateTimeString,
  orgAddressAlias: z.string(),
  district: z.string(),
  // ACCEPTED = worker picked, still in 30-min window; COMMITTED = locked.
  committed: z.boolean(),
});

export const employerApplicantSchema = applicantRowSchema.extend({
  postingId: uuidSchema,
  postingTitle: z.string(),
  orgAddressId: uuidSchema.nullable(),
  orgAddressAlias: z.string().nullable(),
  district: z.string().nullable(),
  // Best-effort one-line work-experience label for the table ("2 năm pha chế").
  experienceSummary: z.string().nullable(),
  // Present only on the "scheduled" tab.
  scheduledSlot: applicantScheduledSlotSchema.nullable(),
});

export const employerApplicantsQuerySchema = pagePaginationQuerySchema.extend({
  tab: employerApplicantTabSchema.optional(),
  orgAddressId: uuidSchema.optional(),
  search: z.string().optional(),
  sort: z.enum(["latest"]).optional(),
});

export const employerApplicantsListResponseSchema = pagedResponseSchema(
  employerApplicantSchema,
);

/** Tab badge counts (one query, independent of the active page). */
export const employerApplicantCountsSchema = z.object({
  new: z.number().int().min(0),
  invited: z.number().int().min(0),
  scheduled: z.number().int().min(0),
  rejected: z.number().int().min(0),
});

export const rejectApplicationsBatchRequestSchema = z.object({
  applicationIds: z.array(uuidSchema).min(1).max(100),
});
export const rejectApplicationsBatchResponseSchema = z.object({
  rejectedCount: z.number().int().min(0),
});

// Compact "task" item for the employer dashboard: every application that is
// still in APPLIED state across the org's OPEN postings. The dashboard shows
// a small page (limit ~10) and a `totalCount` so we can render "+N nữa" when
// there are more pending than fit.
export const pendingApplicantSchema = z.object({
  applicationId: uuidSchema,
  postingId: uuidSchema,
  postingTitle: z.string(),
  workerMaskedName: z.string(),
  workerDistrict: z.string().nullable(),
  appliedAt: isoDateTimeString,
});

export const pendingApplicantsResponseSchema = z.object({
  items: z.array(pendingApplicantSchema),
  totalCount: z.number().int().min(0),
});

/**
 * Full applicant CV/profile shown to employers in the worker-profile modal.
 * Sensitive identifiers (full name, phone, email) are only populated once
 * the invite has been COMMITTED — before that we show maskedName and
 * `contact: null`. Everything else (skills, experience, bio, photos, etc.)
 * is visible regardless of invite state so employers can assess fit before
 * spending a credit.
 */
/** The full anonymized worker CV an employer sees before committing. Shared by
 *  the applicant-profile modal and the talent-sourcing detail (docs/01-product.md) so both
 *  reuse the same render. Identity (fullName) only filled post-commit. */
export const applicantCvSchema = z.object({
  userId: uuidSchema,
  maskedName: z.string(),
  fullName: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  fullBodyImageUrls: z.array(z.string()),
  birthYear: z.number().int().nullable(),
  age: z.number().int().nullable(),
  gender: genderSchema.nullable(),
  currentDistrict: z.string().nullable(),
  heightCm: z.number().int().nullable(),
  weightKg: z.number().int().nullable(),
  bioShort: z.string().nullable(),
  desiredPositions: z.array(workerPositionSchema),
  employmentTypes: z.array(employmentTypeSchema),
  desiredAreas: z.array(z.string()),
  desiredSalaryMin: salaryOutputSchema.nullable(),
  desiredSalaryMax: salaryOutputSchema.nullable(),
  desiredSalaryType: salaryTypeSchema.nullable(),
  availableFrom: availableFromSchema.nullable(),
  skills: z.array(z.string()),
  advantages: z.array(z.string()),
  languages: z.array(languageEntrySchema).nullable(),
  hasMotorbike: z.boolean(),
  hasVisibleTattoo: z.boolean(),
  education: educationLevelSchema.nullable(),
  hometown: provinceCodeSchema.nullable(),
  livingArrangement: livingArrangementSchema.nullable(),
  experiences: z.array(workerExperienceSchema),
  kycStatus: kycStatusSchema,
  kycVerifiedAt: isoDateTimeString.nullable(),
  totalApplications: z.number().int(),
  totalInvites: z.number().int(),
  completionPct: z.number().int(),
  ratingAvg: z.number().nullable(),
  ratingCount: z.number().int(),
  memberSince: isoDateTimeString,
});
export type ApplicantCv = z.infer<typeof applicantCvSchema>;

export const applicantProfileSchema = z.object({
  application: applicationSchema,
  worker: applicantCvSchema,
  // True once an invite has been sent. Distinct from contact availability —
  // contact + full name only unlock after the invite COMMITS.
  invited: z.boolean(),
  committedAt: isoDateTimeString.nullable(),
  contact: z
    .object({
      phone: z.string().nullable(),
      email: z.string().nullable(),
      fullName: z.string().nullable(),
    })
    .nullable(),
});

export const inviteApplicationResponseSchema = z.object({
  application: applicationSchema,
  worker: publicWorkerSummarySchema,
  contact: z.object({
    phone: z.string().nullable(),
    email: z.string().nullable(),
    fullName: z.string().nullable(),
  }),
  creditBalanceAfter: z.number().int(),
  alreadyInvited: z.boolean(),
});

export type ApplicationState = z.infer<typeof applicationStateSchema>;
export type ApplicationEventType = z.infer<typeof applicationEventTypeSchema>;
export type ApplicationEvent = z.infer<typeof applicationEventSchema>;
export type Application = z.infer<typeof applicationSchema>;
export type ApplyPostingRequest = z.infer<typeof applyPostingRequestSchema>;
export type ApplyPostingResponse = z.infer<typeof applyPostingResponseSchema>;
export type InviteSummary = z.infer<typeof inviteSummarySchema>;
export type MyApplicationListItem = z.infer<typeof myApplicationListItemSchema>;
export type MyApplicationDetail = z.infer<typeof myApplicationDetailSchema>;
export type MyApplicationListResponse = z.infer<
  typeof myApplicationListResponseSchema
>;
export type ApplicantRow = z.infer<typeof applicantRowSchema>;
export type ApplicantListResponse = z.infer<typeof applicantListResponseSchema>;
export type PendingApplicant = z.infer<typeof pendingApplicantSchema>;
export type PendingApplicantsResponse = z.infer<
  typeof pendingApplicantsResponseSchema
>;
export type ApplicantProfile = z.infer<typeof applicantProfileSchema>;
export type InviteApplicationResponse = z.infer<
  typeof inviteApplicationResponseSchema
>;
export type ApplicantScheduledSlot = z.infer<
  typeof applicantScheduledSlotSchema
>;
export type EmployerApplicant = z.infer<typeof employerApplicantSchema>;
export type EmployerApplicantsQuery = z.infer<
  typeof employerApplicantsQuerySchema
>;
export type EmployerApplicantsListResponse = z.infer<
  typeof employerApplicantsListResponseSchema
>;
export type EmployerApplicantCounts = z.infer<
  typeof employerApplicantCountsSchema
>;
export type RejectApplicationsBatchRequest = z.infer<
  typeof rejectApplicationsBatchRequestSchema
>;
export type RejectApplicationsBatchResponse = z.infer<
  typeof rejectApplicationsBatchResponseSchema
>;
